import { alg, Graph } from 'graphlib';
import React, { useEffect, useMemo, useRef, useState } from 'react';
import { useSelector } from 'react-redux';
import { usePageCRUDActions } from '@mm/state/actions/PageCRUDActions';
import { useWidgetCRUDActions } from '@mm/state/actions/WidgetCRUDActions';
import ButtonModel, {
  ButtonType,
  PageButtonConfig,
} from '@mm/state/model/ButtonModel';
import PageModel from '@mm/state/model/PageModel';
import WidgetModel from '@mm/state/model/WidgetModel';
import { activeChatbotIdSelector } from '@mm/state/selectors/viewSelectors';
import { StoreState } from '@mm/state/store-interfaces';

// Note: the context is not available for the widgets from regular Bot Builder.
// That is why casting as optional using Partial to ensure needed checks in the consumers
type PathGraphContextType = Partial<ReturnType<typeof useNewGraphInstance>>;

export const getDialogueNodeName = (dialogueId: number | undefined) => {
  return `d-${dialogueId}`;
};

export const getWidgetNodeName = (widgetId: number | undefined) => {
  return `w-${widgetId}`;
};

export const getButtonNodeName = (buttonId: number | undefined) => {
  return `b-${buttonId}`;
};

export const getIdFromName = (name: string) => Number(name.slice(2));

export const getNodeName = (
  id: number | undefined,
  type: 'dialogue' | 'widget' | 'button',
) => {
  const getName = () => {
    switch (type) {
      case 'dialogue':
        return getDialogueNodeName(id);
      case 'widget':
        return getWidgetNodeName(id);
      case 'button':
        return getButtonNodeName(id);
    }
  };

  return getName();
};

export const useNewGraphInstance = (promoterPageId: number | undefined) => {
  const graphRef = useRef<null | Graph>(null);

  const [lastUpdate, setLastUpdate] = useState(performance.now());

  const widgetCRUDActions = useWidgetCRUDActions();
  const pageCRUDActions = usePageCRUDActions();

  const chatbotId = useSelector(activeChatbotIdSelector);
  const loadedAndNotDeletedDialogues = useSelector(
    (state: StoreState) => state.page.entities,
  );

  const addDialogueNode = (dialogueId: number | undefined) => {
    const g = graphRef.current;
    if (!g) return;

    if (dialogueId) {
      g.setNode(getNodeName(dialogueId, 'dialogue'));
    }
  };

  useEffect(() => {
    const g = new Graph({ directed: true, multigraph: true });
    graphRef.current = g;
    graphRef.current.setGraph(String(promoterPageId));

    addDialogueNode(promoterPageId);
    setLastUpdate(performance.now());
  }, [promoterPageId]);

  const getAllPredecessors = ({
    id,
    type,
    nodeName,
    predecessors = { nodes: {}, isCircular: false },
  }: {
    id?: number;
    type?: 'dialogue' | 'widget' | 'button';
    nodeName?: string;
    predecessors?: { nodes: Record<string, boolean>; isCircular: boolean };
  }): { nodes: Record<string, boolean>; isCircular: boolean } => {
    const g = graphRef.current;
    if (!g) return { nodes: {}, isCircular: false };

    const currentNodeName = nodeName || getNodeName(id!, type!);

    const isCircular = !!predecessors.nodes[currentNodeName];

    if (isCircular) {
      return {
        nodes: predecessors.nodes,
        isCircular,
      };
    }

    predecessors.nodes[currentNodeName] = true;
    const currentNodePredecessors = g.predecessors(currentNodeName) || [];

    const currentAndPredecessors = currentNodePredecessors
      .map(p => getAllPredecessors({ nodeName: p, predecessors }))
      .reduce(
        (acc, val) => ({
          nodes: { ...val.nodes, ...acc.nodes },
          isCircular: acc.isCircular || val.isCircular,
        }),
        { ...predecessors },
      );

    return currentAndPredecessors;
  };

  const addPotentiallyCircularData = (fromName: string, toName: string) => {
    const g = graphRef.current;
    if (!g) return;

    if (!g.hasNode(toName)) {
      g.setNode(toName);
    }

    g.setEdge(fromName, toName, { circular: false });

    // Note: isAcyclic is more effecient than finding all cycles.
    // If there are cycles, we step down to somewhat slower algorytms of finding all paths.
    // We need multiple paths, since only some paths in the graph might be circular.
    if (!alg.isAcyclic(g)) {
      const paths = alg.findCycles(g);
      paths.forEach(pathNodes => {
        if (pathNodes.some(n => n === toName)) {
          g.setEdge(fromName, toName, { circular: true });
        }
      });
    }
    setLastUpdate(performance.now());
  };

  const addWidgetButtonsToGraph = (
    widgetId: number,
    buttons: ButtonModel[],
  ) => {
    const g = graphRef.current;
    if (!(g && widgetId && buttons.length)) return;

    const widgetNodeName = getNodeName(widgetId, 'widget');

    for (const button of buttons) {
      const buttonNodeName = getButtonNodeName(button.id);
      g.setNode(buttonNodeName);
      g.setEdge(widgetNodeName, buttonNodeName);

      if ((button?.config as PageButtonConfig)?.pageId) {
        const targetDialogueId = getNodeName(
          (button?.config as PageButtonConfig).pageId,
          'dialogue',
        );
        addPotentiallyCircularData(buttonNodeName, targetDialogueId);
      }
    }

    setLastUpdate(performance.now());
  };

  const addDialogueWidgetsToGraph = (widgets: WidgetModel[]) => {
    const g = graphRef.current;
    if (!(g && widgets.length)) return;

    const dialogueNodeName = getNodeName(widgets[0].pageId, 'dialogue');

    if (!g.hasNode(getNodeName(widgets[0].pageId, 'dialogue'))) {
      g.setNode(dialogueNodeName);
    }

    for (const widget of widgets) {
      const widgetNodeName = getNodeName(widget.id, 'widget');
      g.setNode(widgetNodeName);
      g.setEdge(dialogueNodeName, widgetNodeName, { circular: false });

      if (widget.config?.pageId) {
        const targetDialogueId = getNodeName(widget.config?.pageId, 'dialogue');
        addPotentiallyCircularData(widgetNodeName, targetDialogueId);
      }

      if (widget.buttons?.length) {
        addWidgetButtonsToGraph(widget.id, widget.buttons);
      }

      if (widget.config.entries?.length) {
        const allWidgetButtonsArray =
          widget.config.entries?.map(e => e.buttons) ||
          ([[]] as ButtonModel[][]);
        const allWidgetButtons = ([] as ButtonModel[]).concat(
          ...allWidgetButtonsArray,
        );

        addWidgetButtonsToGraph(widget.id, allWidgetButtons);
      }
    }
    setLastUpdate(performance.now());
  };

  const setActiveNode = async (newNodeValue: {
    id: number;
    type: 'button' | 'widget';
    dialogueId?: number;
    action: 'open' | 'close';
    getNextDialogue?: boolean;
    getNextDialogueWidgets?: boolean;
  }) => {
    const g = graphRef.current;
    if (!g) return;

    const {
      id,
      type,
      dialogueId,
      action,
      getNextDialogue = false,
      getNextDialogueWidgets = true,
    } = newNodeValue;

    if (action === 'open' && dialogueId) {
      let freshlyLoadedPages: PageModel[] | undefined;
      if (getNextDialogue) {
        freshlyLoadedPages = await pageCRUDActions.retrieveAll(chatbotId);
      }

      const isDialogueIdValid = (
        freshlyLoadedPages || loadedAndNotDeletedDialogues
      ).some(d => d.id === dialogueId);
      if (isDialogueIdValid && getNextDialogueWidgets) {
        await widgetCRUDActions.retrieveAll(dialogueId, true);
      }
    }

    g.setNode(getNodeName(id, type), action === 'open' ? true : undefined);

    setLastUpdate(performance.now());
  };

  const turnAllActiveNodesOff = () => {
    const g = graphRef.current;
    if (!g) return;

    const nodes = g.nodes();

    if (nodes) {
      for (const node of nodes) {
        if (g.node(node)) {
          g.setNode(node, undefined);
          setLastUpdate(performance.now());
        }
      }
    }
  };

  const turnSuccessorsActiveNodesOff = (startingNodeName: string) => {
    const g = graphRef.current;
    if (!g) return;

    const outEdges = g.outEdges(startingNodeName) || [];

    for (const edge of outEdges) {
      const edgeLabel = g.edge(edge);
      const successorNodeName = edge.w;

      if (g.node(successorNodeName)) {
        g.setNode(successorNodeName, undefined);
        setLastUpdate(performance.now());
      }

      if (edgeLabel && successorNodeName && !edgeLabel.circular) {
        turnSuccessorsActiveNodesOff(successorNodeName);
      }
    }
  };

  const recursivelyRemoveNodesAndEdges = (
    startingNodeName: string,
    actionType: 'deleteSuccessors' | 'delete',
  ) => {
    const g = graphRef.current;
    if (!g) return;

    const outEdges = g.outEdges(startingNodeName) || [];

    for (const edge of outEdges) {
      const edgeLabel = g.edge(edge);
      const successorNodeName = edge.w;
      g.removeEdge(edge);

      if (edgeLabel && successorNodeName && !edgeLabel.circular) {
        recursivelyRemoveNodesAndEdges(successorNodeName, 'delete');
      }
    }

    const inEdges = g.inEdges(startingNodeName) || [];

    if (actionType === 'delete' && !inEdges.length) {
      g.removeNode(startingNodeName);
    }

    setLastUpdate(performance.now());
  };

  const removeNodeAndSuccessors = ({
    id,
    type,
    parentId,
    parentType,
    actionType = 'deleteSuccessors',
  }: {
    id: number | undefined;
    type: 'dialogue' | 'widget' | 'button';
    parentId?: number;
    parentType?: 'dialogue' | 'widget' | 'button';
    actionType?: 'deleteSuccessors' | 'delete';
  }) => {
    const g = graphRef.current;
    if (!(g && id)) return;

    const nodeName = getNodeName(id, type);
    const parentNodeName = parentType
      ? getNodeName(parentId, parentType)
      : undefined;

    if (actionType === 'delete' && parentNodeName) {
      g.hasEdge(parentNodeName, nodeName) &&
        g.removeEdge(parentNodeName, nodeName);
    }

    recursivelyRemoveNodesAndEdges(nodeName, actionType);
  };

  const getActiveButtonFromWidgetButtons = (buttons: ButtonModel[]) => {
    const g = graphRef.current;
    if (!(g && buttons.length)) return undefined;

    for (const button of buttons) {
      const buttonNodeLabel = g.node(getButtonNodeName(button.id));
      if (buttonNodeLabel) {
        return button;
      }
    }

    return undefined;
  };

  const getActiveButtonFromWidget = (widget?: WidgetModel) => {
    const defaults = [[]] as ButtonModel[][];

    const entryWidgetButtonsArray =
      widget?.config.entries?.map(e => e.buttons) || defaults;

    const entryWidgetButtonsArrayFlattened = ([] as ButtonModel[]).concat(
      ...entryWidgetButtonsArray,
    );

    const widgetButtons = entryWidgetButtonsArrayFlattened.length
      ? entryWidgetButtonsArrayFlattened
      : widget?.buttons || ([] as ButtonModel[]);

    return getActiveButtonFromWidgetButtons(widgetButtons);
  };

  const isWidgetActive = (widget: WidgetModel) => {
    const g = graphRef.current;
    if (!g) return false;

    const isActiveWidget =
      !!widget?.config?.pageId &&
      !!g.node(getNodeName(widget.id, 'widget')) &&
      loadedAndNotDeletedDialogues.some(d => d.id === widget.config.pageId);

    const entryWidgetButtonsArray =
      widget.config.entries?.map(e => e.buttons) || ([[]] as ButtonModel[][]);
    const entryWidgetButtonsArrayFlattened = ([] as ButtonModel[]).concat(
      ...entryWidgetButtonsArray,
    );

    const widgetButtons = entryWidgetButtonsArrayFlattened.length
      ? entryWidgetButtonsArrayFlattened
      : widget.buttons;
    const activeButton = getActiveButtonFromWidgetButtons(widgetButtons);

    const isActiveButtonValid =
      !!activeButton &&
      [ButtonType.PAGE, ButtonType.OPT_IN].some(t => t === activeButton.type) &&
      !!(activeButton.config as PageButtonConfig)?.pageId &&
      loadedAndNotDeletedDialogues.some(
        d => d.id === (activeButton.config as PageButtonConfig)?.pageId,
      );

    return isActiveWidget || isActiveButtonValid;
  };

  const getWidgetActiveDialogueInfo = (widget: WidgetModel) => {
    const g = graphRef.current;
    if (!g) return { nextLevelDialogueId: undefined, isCircular: false };

    const widgetNodeName = getNodeName(widget.id, 'widget');

    const dialogueIdFromActiveWidget =
      g.node(widgetNodeName) && widget.config?.pageId;

    const activeButton = getActiveButtonFromWidgetButtons(widget.buttons);

    const buttonNodeName = getNodeName(activeButton?.id, 'button');

    const nextLevelDialogueId =
      dialogueIdFromActiveWidget ||
      (activeButton?.config as PageButtonConfig)?.pageId;

    const startingNodeName = dialogueIdFromActiveWidget
      ? widgetNodeName
      : buttonNodeName;

    const edgeToDialogueLabel = g.edge(
      startingNodeName,
      getNodeName(nextLevelDialogueId, 'dialogue'),
    );

    const isCircular = !!edgeToDialogueLabel?.circular;
    return { nextLevelDialogueId, isCircular };
  };

  const deselectWidgetButtons = (buttons: ButtonModel[]) => {
    const g = graphRef.current;
    if (!(g && buttons.length)) return undefined;

    for (const button of buttons) {
      setActiveNode({
        id: button.id,
        type: 'button',
        action: 'close',
      });
    }
  };

  return useMemo(() => {
    return {
      pathGraph: graphRef.current,
      pathGraphLastUpdate: lastUpdate,
      setActiveNode,
      removeNodeAndSuccessors,
      getActiveButtonFromWidgetButtons,
      getActiveButtonFromWidget,
      isWidgetActive,
      deselectWidgetButtons,
      getWidgetActiveDialogueInfo,
      addDialogueWidgetsToGraph,
      addWidgetButtonsToGraph,
      getAllPredecessors,
      turnAllActiveNodesOff,
      turnSuccessorsActiveNodesOff,
    };
  }, [graphRef.current, lastUpdate, loadedAndNotDeletedDialogues.length]);
};

export const PathGraphContext = React.createContext({} as PathGraphContextType);
